import { useCallback, useEffect, useState } from 'react';
import getAuth0Options from './getAuth0Options';
import Auth0Lock from 'auth0-lock';
import { jwtDecode } from 'jwt-decode';
import IJwtToken from '../models/jwt-token.interface';
import { TLanguage } from '../models/languages.type';
import ISubscription from '../models/subscription.interface';
import { isNil } from '../misc/is-nil';

const localHostnames = ['localhost', 'endtoend.docker-dev.pct.local', 'endtoend.docker-dev2.pct.local'];

const getRedirectURL = ({ subdomain, token, redirectUrlFromState, param }: IGetUrlArgument): string | null => {
  const domain = process.env.REACT_APP_DOMAIN!;
  const enableLocalhost = process.env.REACT_APP_ENABLE_LOCALHOST;

  try {
    const url = new URL(atob(redirectUrlFromState!));
    if (localHostnames.includes(url.hostname) && enableLocalhost === 'true') {
      url.searchParams.append('access_token', token);
      url.searchParams.append('param', param!);
      return url.toString();
    }
    if (domain === url.hostname.split('.')[1]) {
      url.searchParams.append('access_token', token);
      url.searchParams.append('param', param!);
      return url.toString();
    }
    return null;
  } catch (e) {
    if (!isNil(subdomain) && !isNil(token)) {
      return `https://${subdomain}.${domain}.hu/?access_token=${token}`;
    }
    return null;
  }
};

const useAuth0: (language: TLanguage) => {
  subscriptions: ISubscription[] | null;
  tryRedirect: (subdomain: string, tokenParam?: string, appState?: string) => boolean;
} = (language: TLanguage) => {
  const [subscriptions, setSubscriptions] = useState<ISubscription[] | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const tryRedirect = useCallback(
    (subdomain: string, tokenParam?: string, appState?: string) => {
      const queryParams = isNil(appState) ? null : new URLSearchParams(appState);

      const redirectUrlFromState = queryParams?.get('redirect') ?? null;
      const param = queryParams?.get('param') ?? null;

      const url = getRedirectURL({ subdomain, token: tokenParam ?? token!, redirectUrlFromState, param });

      if (!isNil(url)) {
        console.info('Redirect to:', url);
        window.location.href = url;
        return true;
      }
      return false;
    },
    [token]
  );

  useEffect(() => {
    const lock = new Auth0Lock(
      process.env.REACT_APP_AUTH0_CLIENT_ID!,
      process.env.REACT_APP_AUTH0_DOMAIN!,
      getAuth0Options(window.location.search, language ?? 'en')
    );

    if (!isNil(language)) {
      const authentication = /access_token/.test(window.location.href);

      if (authentication) {
        lock.resumeAuth(window.location.hash, (error, authResult) => {
          if (!isNil(authResult)) {
            const newSubscriptions = jwtDecode<IJwtToken>(authResult.accessToken)['http://api.hr.hu/subscriptions'];

            let redirected = false;

            if (newSubscriptions.length === 1 || !isNil(authResult.appState)) {
              redirected = tryRedirect(
                newSubscriptions[0].subdomain,
                authResult.accessToken,
                typeof authResult.appState === 'string' ? authResult.appState : undefined
              );
            }

            if (!redirected) {
              setSubscriptions(newSubscriptions);
              setToken(authResult.accessToken);
            }
          }
        });
      } else if (!subscriptions) {
        lock.show();
      }
    }
    return (): void => lock.hide();
  }, [language, subscriptions, tryRedirect]);

  return { subscriptions, tryRedirect };
};

export default useAuth0;

interface IGetUrlArgument {
  subdomain: string;
  token: string;
  redirectUrlFromState: string | null;
  param: string | null;
}
